import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Sidebar from './components/Sidebar';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Campanhas from './pages/Campanhas';
import CampanhaProdutos from './pages/CampanhaProdutos';
import Estudio from './pages/Estudio';
import EstudioCampanha from './pages/EstudioCampanha';
import { estaNoEstudio, linkEstudio } from './utils/estudio';
import Usuarios from './pages/Usuarios';
import Perfis from './pages/Perfis';
import MeuPerfil from './pages/MeuPerfil';
import LogAtividades from './pages/LogAtividades';
import PlaquinhasPage from './pages/PlaquinhasPage';
import CampanhasVendedor from './pages/CampanhasVendedor';
import CampanhaVendedorDetalhe from './pages/CampanhaVendedorDetalhe';
import RelatorioComissaoExtra from './pages/RelatorioComissaoExtra';
import Aniversariantes from './pages/Aniversariantes';
import AnalisesVenda from './pages/AnalisesVenda';
import MonitorRamais from './pages/MonitorRamais';
import TorcidaPublico from './pages/torcida/TorcidaPublico';
import TorcidaRanking from './pages/torcida/TorcidaRanking';
import TorcidaTv from './pages/torcida/TorcidaTv';
import TorcidaAdmin from './pages/TorcidaAdmin';
import Land from './pages/Land';
import Parceiros from './pages/Parceiros';
import Checklist from './pages/Checklist';
import VendasCampanhas from './pages/VendasCampanhas';
import Premiacoes from './pages/Premiacoes';
import Demandas from './pages/Demandas';

// Ordem de prioridade pra redirecionar usuario sem permissao na rota alvo.
// Cai sempre na primeira rota que o usuario tem direito; ultimo recurso e
// /meu-perfil (sem permissao). Evita loop quando usuario nao tem dashboard.
const ROTAS_FALLBACK = [
  { path: '/dashboard', permissao: 'dashboard' },
  { path: '/campanhas', permissao: 'campanhas' },
  { path: '/plaquinhas', permissao: 'plaquinhas_gerar' },
  { path: '/campanhas-vendedor', permissao: 'campanhas_vendedor' },
  { path: '/campanhas-vendedor', permissao: 'campanhas_vendedor_loja' },
  { path: '/premiacoes', permissao: 'premiacoes' },
  { path: '/premiacoes', permissao: 'premiacoes_loja' },
  { path: '/meu-perfil', permissao: null },
];

function usePrimeiraRotaPermitida() {
  const { temPermissao } = useAuth();
  for (const r of ROTAS_FALLBACK) {
    if (!r.permissao || temPermissao(r.permissao)) return r.path;
  }
  return '/meu-perfil';
}

function PrivateRoute({ children, permissao }) {
  const { user, loading, temPermissao } = useAuth();
  const fallback = usePrimeiraRotaPermitida();
  if (loading) return <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}><div className="spinner-border" style={{ color: '#6B2C5C' }} /></div>;
  if (!user) return <Navigate to="/login" replace />;
  // permissao pode ser string ou array (OR — basta ter uma)
  const temAcesso = !permissao || (Array.isArray(permissao) ? permissao.some(p => temPermissao(p)) : temPermissao(permissao));
  if (!temAcesso) return <Navigate to={fallback} replace />;
  return children;
}

// Dentro do Estúdio de Criação (/estudio) o layout troca menu e cores (utils/estudio.js).
function AppLayout({ children }) {
  const { pathname } = useLocation();
  return (
    <div className={`layout${estaNoEstudio(pathname) ? ' modo-estudio' : ''}`}>
      <Sidebar />
      <main className="main-content">{children}</main>
    </div>
  );
}

function ParaEstudio({ aba }) {
  const { id } = useParams();
  return <Navigate to={linkEstudio(id, aba)} replace />;
}

function AppRoutes() {
  const { user } = useAuth();
  const fallback = usePrimeiraRotaPermitida();
  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to={fallback} replace /> : <Login />} />

      {/* Torcida Centrão — rotas PÚBLICAS (sem login, sem sidebar). QR único por loja:
          o sistema detecta se o CPF é de funcionário (rh) ou cliente (clientecad). */}
      <Route path="/torcida/:slug" element={<TorcidaPublico />} />
      <Route path="/torcida/:slug/ranking" element={<TorcidaRanking />} />
      <Route path="/torcida/:slug/tv" element={<TorcidaTv />} />
      <Route path="/dashboard" element={<PrivateRoute permissao="dashboard"><AppLayout><Dashboard /></AppLayout></PrivateRoute>} />
      <Route path="/campanhas" element={<PrivateRoute permissao="campanhas"><AppLayout><Campanhas /></AppLayout></PrivateRoute>} />
      <Route path="/campanhas/:id/produtos" element={<PrivateRoute permissao="campanhas"><AppLayout><CampanhaProdutos /></AppLayout></PrivateRoute>} />
      {/* Estúdio de Criação: stories, pôster e catálogo de uma campanha, com a identidade dela. */}
      <Route path="/estudio" element={<PrivateRoute permissao="campanhas_editar"><AppLayout><Estudio /></AppLayout></PrivateRoute>} />
      <Route path="/estudio/:id" element={<PrivateRoute permissao="campanhas_editar"><AppLayout><EstudioCampanha /></AppLayout></PrivateRoute>} />
      {/* Endereços antigos das três ferramentas: agora são abas do Estúdio. */}
      <Route path="/campanhas/:id/catalogo" element={<ParaEstudio aba="catalogo" />} />
      <Route path="/campanhas/:id/poster" element={<ParaEstudio aba="poster" />} />
      <Route path="/campanhas/:id/story" element={<ParaEstudio aba="stories" />} />
      <Route path="/campanhas/:id/comissao-extra" element={<PrivateRoute permissao="relatorio_comissao_extra"><AppLayout><RelatorioComissaoExtra /></AppLayout></PrivateRoute>} />
      <Route path="/plaquinhas" element={<PrivateRoute permissao="plaquinhas_gerar"><AppLayout><PlaquinhasPage /></AppLayout></PrivateRoute>} />
      <Route path="/campanhas-vendedor" element={<PrivateRoute permissao={['campanhas_vendedor', 'campanhas_vendedor_loja']}><AppLayout><CampanhasVendedor /></AppLayout></PrivateRoute>} />
      <Route path="/campanhas-vendedor/:id" element={<PrivateRoute permissao={['campanhas_vendedor', 'campanhas_vendedor_loja']}><AppLayout><CampanhaVendedorDetalhe /></AppLayout></PrivateRoute>} />
      <Route path="/torcida/admin" element={<PrivateRoute permissao={['torcida_ver', 'torcida_loja']}><AppLayout><TorcidaAdmin /></AppLayout></PrivateRoute>} />
      <Route path="/checklist" element={<PrivateRoute permissao={['checklist_gerenciar', 'checklist_loja']}><AppLayout><Checklist /></AppLayout></PrivateRoute>} />
      <Route path="/vendas-campanhas" element={<PrivateRoute permissao="vendas_campanhas"><AppLayout><VendasCampanhas /></AppLayout></PrivateRoute>} />
      <Route path="/premiacoes" element={<PrivateRoute permissao={['premiacoes', 'premiacoes_loja']}><AppLayout><Premiacoes /></AppLayout></PrivateRoute>} />
      <Route path="/demandas" element={<PrivateRoute permissao="demandas"><AppLayout><Demandas /></AppLayout></PrivateRoute>} />
      <Route path="/land" element={<PrivateRoute permissao={['land_ver', 'land_gerenciar']}><AppLayout><Land /></AppLayout></PrivateRoute>} />
      <Route path="/parceiros" element={<PrivateRoute permissao={['parceiros_ver', 'parceiros_gerenciar']}><AppLayout><Parceiros /></AppLayout></PrivateRoute>} />
      <Route path="/usuarios" element={<PrivateRoute permissao="usuarios"><AppLayout><Usuarios /></AppLayout></PrivateRoute>} />
      <Route path="/perfis" element={<PrivateRoute permissao="perfis"><AppLayout><Perfis /></AppLayout></PrivateRoute>} />
      <Route path="/meu-perfil" element={<PrivateRoute><AppLayout><MeuPerfil /></AppLayout></PrivateRoute>} />
      <Route path="/log-atividades" element={<PrivateRoute permissao="log_atividades"><AppLayout><LogAtividades /></AppLayout></PrivateRoute>} />
      <Route path="/aniversariantes" element={<PrivateRoute permissao="aniversariantes"><AppLayout><Aniversariantes /></AppLayout></PrivateRoute>} />
      <Route path="/analises-venda" element={<PrivateRoute permissao="analises_venda"><AppLayout><AnalisesVenda /></AppLayout></PrivateRoute>} />
      <Route path="/monitor-ramais" element={<PrivateRoute permissao="monitor_ramais"><AppLayout><MonitorRamais /></AppLayout></PrivateRoute>} />
      <Route path="*" element={<Navigate to={user ? fallback : '/login'} replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </AuthProvider>
  );
}
